import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useChatContext } from "@/context/ChatContext";
import { cn } from "@/lib/utils";
import { MessageSquare, ChevronRight, ArchiveRestore, Archive } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import type { ChatConversation } from "@/types/chat";
import { useToast } from "@/hooks/useToast";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

export function ArchivedConversationsList() {
  const { loadConversation, getCurrentConversation, chats, isLoading, setChats } = useChatContext();
  const currentConversation = getCurrentConversation("research_assistant");
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-2 px-2 py-2">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-[200px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[236px]">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-2"
          aria-expanded={isOpen}
        >
          <div className="flex items-center">
            <Archive className="h-4 w-4" />
            <span className="ml-2 text-sm">Archived Conversations</span>
          </div>
          <ChevronRight
            className={cn("h-4 w-4 transition-transform duration-200", isOpen && "transform rotate-90")}
            aria-hidden="true"
          />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="py-2" role="list" aria-label="Archived conversations list">
            {chats
              .filter((chat: ChatConversation) => chat.discarded_at !== null)
              .filter((chat: ChatConversation) => (chat.kind ?? "research_assistant") === "research_assistant")
              .sort((a, b) => new Date(b.discarded_at).getTime() - new Date(a.discarded_at).getTime())
              .slice(0, 10)
              .map((chat: ChatConversation) => {
                return (
                  <div key={chat.id} className="flex w-full max-w-[236px] flex-row items-center">
                    <Button
                      variant={currentConversation?.id === chat.id ? "secondary" : "ghost"}
                      className={cn(
                        "h-9 min-w-0 flex-1 justify-start px-3 py-0",
                        currentConversation?.id === chat.id && "border-2 border-primary font-medium"
                      )}
                      onClick={() => {
                        if (currentConversation?.id === chat.id) {
                          return;
                        }
                        loadConversation(chat.id, "research_assistant");
                      }}
                      role="listitem"
                    >
                      <MessageSquare
                        className={cn("h-4 w-4 shrink-0", currentConversation?.id === chat.id && "text-primary")}
                      />
                      <span className="ml-2 overflow-hidden text-ellipsis max-w-[180px]">
                        {chat.title || format(new Date(chat.created_at), "PPp")}
                      </span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        if (!window.confirm("Are you sure you want to restore this conversation?")) {
                          return;
                        }

                        try {
                          await fetch("/api/v1/chats/unarchive", {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              "X-CSRF-Token":
                                document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
                            },
                            body: JSON.stringify({ id: chat.id }),
                          });

                          // 👇 Update local chat state
                          setChats((prevChats) =>
                            prevChats.map((c) =>
                              c.id === chat.id ? { ...c, discarded_at: null } : c
                            )
                          );

                          toast({
                            title: "Chat restored",
                            description: "The conversation has been restored successfully.",
                          });
                        } catch (error) {
                          Honeybadger.notify(error);
                          toast({
                            title: "Error",
                            description: "Failed to restore the conversation.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ArchiveRestore className="h-4 w-4" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Unarchive chat</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Button>
                  </div>
                );
              })}
        </div>
      </CollapsibleContent>
    </Collapsible>
    </div>
  );
}
